import type { ServiceDetail } from "@/types/detail";
import { DEV_WORKFLOW } from "@/data/detail.shared";
import {
  PenTool,
  Server,
  BrainCircuit,
  Workflow,
  Cloud,
  LifeBuoy,
  Gauge,
  ShieldCheck,
  Zap,
  Users,
  Rocket,
  LineChart,
  Layers,
  Clock,
  TrendingUp, 
  Link
} from "lucide-react";

export const servicesB: ServiceDetail[] = [
  {
    slug: "ui-ux-design",
    title: "UI/UX Design",
    category: "Design",
    tagline: "Exceptional digital products begin with exceptional user experiences. We design intuitive, accessible and visually engaging interfaces that makes complex tasks simple and enjoyable.",
    icon: PenTool,
    accent: "#4F46E5",
    overview:
      "Exceptional digital products begin with exceptional user experiences. We design intuitive, accessible and visually engaging interfaces that make complex tasks simple and enjoyable. Through user research, interaction design, wireframing, prototyping and usability testing, we create experiences that increase customer satisfaction, improve productivity and support measurable business outcomes.",
    problems: [
      "Users struggle to complete tasks due to confusing navigation and inconsistent interfaces.",
      "Low conversion rates caused by poor user experience and unclear user journeys.",
      "High customer support requests resulting from difficult-to-use applications.",
      "Existing products that no longer meet modern usability and accessibility expectations.",
    ],
    idealCustomers: [
      "Businesses launching new digital products.",
      "Organisations redesigning existing websites or applications.",
      "Startups validating product ideas before development.",
      "Enterprises modernising legacy software.",
      "Public sector organisations requiring accessible digital services.",
    ],
    benefits: [
      {
          title: "Improve User Satisfaction",
          description: "Create intuitive experiences that help users complete tasks quickly and confidently.",
          icon: Users,
      },
      {
          title: "Increase Conversion Rates",
          description: "Optimised user journeys encourage visitors to become customers and customers to complete key actions.",
          icon: TrendingUp,
      },
      {
          title: "Reduce Training & Support",
          description: "Simple, consistent interfaces minimise user errors and reduce support requirements.",
          icon: LifeBuoy,
      },
      {
          title: "Ensure Accessibility",
          description: "Design experiences that are inclusive and usable by people with diverse abilities and devices.",
          icon: ShieldCheck,
      },
    ],
    features: [
      {
          title: "User Research",
          description: "Understand user needs, behaviours and pain points before designing solutions.",
      },
      {
          title: "Wireframes & User Flows",
          description: "Plan intuitive navigation and user journeys before visual design begins.",
      },
      {
          title: "Interactive Prototypes",
          description: "Validate ideas through realistic prototypes before development starts.",
      },
      {
          title: "Responsive Interface Design",
          description: "Design consistent experiences across desktops, tablets and mobile devices.",
      },
      {
          title: "Accessibility-First Design",
          description: "Apply recognised accessibility standards to improve usability for all users.",
      },
      {
          title: "Usability Testing",
          description: "Evaluate designs with real users to identify improvements before implementation.",
      },
    ],
    techStack: ["Figma", "FigJam", "Adobe Illustrator", "Adobe Photoshop", "React", "Tailwind CSS"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "Do you do research or just visuals?", answer: "Both. We start with research and validate designs with users — visuals are the outcome, not the whole process." },
      { question: "Can you create a design system?", answer: "Yes. Scalable, token-driven design systems are a core strength." },
      { question: "Will engineering be able to build your designs?", answer: "We design with implementation in mind and hand off clean, developer-ready specs." },
      { question: "Do you redesign existing products?", answer: "Often. We can improve an existing product or design something new." },
      { question: "How do you measure design success?", answer: "Against outcomes like adoption, task completion and reduced support load." },
      { question: "Can you work with our developers?", answer: "Yes, we collaborate closely with in-house or third-party engineering teams." },
    ],
    relatedServices: ["website-design", "web-applications", "mobile-app-development"],
    relatedIndustries: ["fintech", "healthcare", "education", "retail"],
    caseConcept: {
        title: "Digital Experience Redesign",
        category: "Example Project",
        summary:
            "A growing organisation experienced low user engagement and high task abandonment due to an outdated interface. We redesigned the user experience through research, wireframing, interactive prototyping and usability testing, delivering a modern, accessible interface that improved usability, customer satisfaction and overall business performance.",
        metrics: [
            {
                label: "Task Completion Rate",
                value: "+38%",
            },
            {
                label: "Support Requests",
                value: "-32%",
            },
            {
                label: "User Satisfaction",
                value: "4.8/5",
            },
        ],
        tech: [
            "Figma",
            "React",
            "Tailwind CSS",
        ],
        accent: "#7C3AED",
    },
    image: "/images/services/website-development.webp",
  },
  {
    slug: "api-development",
    title: "API Development",
    category: "Engineering",
    tagline: "Build secure, scalable and well-documented APIs that connect applications, automate business processes and enable seamless data exchange.",
    icon: Server,
    accent: "#0891B2",
    overview:
      "Modern organisations rely on APIs to connect applications, automate workflows and share information securely across systems. We design and develop reliable, secured, high-performance, and scalable REST APIs that enable seamless integration between websites, mobile applications, enterprise software and third-party platforms.",
    problems: [
      "Business applications cannot communicate efficiently due to disconnected systems.",
      "Manual data transfer between systems causing delays and errors.",
      "Legacy software lacking secure integration capabilities.",
      "Poorly designed APIs creating performance, maintenance and security challenges.",
    ],
    idealCustomers: [
      "Businesses integrating multiple software platforms.",
      "Organisations developing web and mobile applications.",
      "Companies modernising legacy systems through APIs.",
      "SaaS providers exposing secure services to customers and partners.",
      "Enterprises automating business workflows across multiple systems.",
    ],
    benefits: [
      {
          title: "Connect Business Systems",
          description: "Enable secure communication between websites, mobile applications, enterprise software and third-party platforms.",
          icon: Link,
      },
      {
          title: "Automate Business Processes",
          description: "Reduce manual work by enabling real-time data exchange and workflow automation.",
          icon: Zap,
      },
      {
          title: "Improve Scalability",
          description: "Build API architectures capable of supporting increasing users, applications and transactions.",
          icon: Rocket,
      },
      {
          title: "Enterprise-Grade Security",
          description: "Protect sensitive information using authentication, authorisation, encryption and secure API development practices.",
          icon: ShieldCheck,
      },
    ],
    features: [
      {
          title: "REST API Development",
          description: "Design and develop scalable REST APIs following industry standards and best practices.",
      },
      {
          title: "Authentication & Authorisation",
          description: "Implement secure authentication using JWT, OAuth 2.0, API keys and role-based access control.",
      },
      {
          title: "Third-Party Integrations",
          description: "Integrate payment gateways, ERP systems, CRM platforms, cloud services and external APIs.",
      },
      {
          title: "Comprehensive API Documentation",
          description: "Provide clear and interactive API documentation using OpenAPI (Swagger) to simplify integration.",
      },
      {
          title: "Performance Optimisation",
          description: "Improve response times through caching, efficient database access and scalable API design.",
      },
      {
          title: "Monitoring & Version Management",
          description: "Support API versioning, logging, monitoring and usage analytics for long-term maintainability.",
      },
    ],
    techStack: ["FastAPI", "Node.js", "Docker", "REST APIs", "PostgreSQL", "Redis"],
    workflow: DEV_WORKFLOW,
    faqs: [
      {
          question: "Can you integrate with our existing software?",
          answer: "Yes. We can integrate your API with existing ERP, CRM, HRMS, accounting software, payment gateways, cloud platforms and other third-party applications. Before development begins, we analyse the available APIs, authentication methods and data structures to ensure a secure and reliable integration.",
      },
      {
          question: "Do you develop secure APIs?",
          answer: "Yes. Security is a core part of every API we build. Depending on your requirements, we implement authentication using JWT, OAuth 2.0 or API keys, apply role-based authorisation, encrypt sensitive data, validate all inputs and follow secure coding practices to protect your systems from common vulnerabilities.",
      },
      {
          question: "Will the API be documented?",
          answer: "Absolutely. Every API includes comprehensive documentation with endpoint descriptions, request and response examples, authentication details, error codes and sample payloads. Where appropriate, we also provide interactive OpenAPI (Swagger) documentation to simplify development and testing.",
      },
      {
          question: "Can APIs support mobile and web applications at the same time?",
          answer: "Yes. A properly designed API can simultaneously serve websites, mobile applications, desktop software and third-party systems, ensuring consistent business logic and data across all platforms.",
      },
      {
          question: "Can you modernise or replace our existing APIs?",
          answer: "Yes. We can review your current APIs, improve performance, strengthen security, introduce versioning, migrate legacy endpoints and redesign the architecture to support future business growth while minimising disruption to existing integrations.",
      },
      {
          question: "Do you provide API maintenance and support?",
          answer: "Yes. We provide ongoing support, including monitoring, bug fixes, security updates, performance optimisation, version management and enhancements as your business requirements evolve.",
      },
    ],
    relatedServices: ["web-applications", "cloud-deployment", "ai-integration"],
    relatedIndustries: ["fintech", "logistics", "manufacturing", "healthcare"],
    caseConcept: {
      title: "Enterprise System Integration Platform",
      category: "Example Project",
      summary:
          "A multi-location organisation required seamless integration between its ERP system, customer portal, mobile application and third-party logistics provider. We developed a secure REST API platform that centralised business logic, automated data synchronisation and enabled real-time communication between all connected systems, significantly improving operational efficiency and reducing manual intervention.",
      metrics: [
          {
              label: "Integrated Systems",
              value: "8",
          },
          {
              label: "Data Synchronisation",
              value: "Real Time",
          },
          {
              label: "Manual Processing",
              value: "-70%",
          },
      ],
      tech: [
          "FastAPI",
          "PostgreSQL",
          "Redis",
      ],
      accent: "#2563EB",
    },
    image: "/images/services/erp_solution.webp",
  },
  {
    slug: "ai-integration",
    title: "AI Solutions & Integration",
    category: "Intelligence",
    tagline: "Integrate practical AI solutions into your business to automate repetitive tasks, improve decision-making and deliver smarter digital experiences.",
    icon: BrainCircuit,
    accent: "#7C3AED",
    overview:
      "Artificial Intelligence is transforming how organisations operate by automating repetitive work, analysing information faster and delivering intelligent user experiences. We help businesses integrate AI by implementing secure, scalable solutions tailored to their business objectives into their existing websites, mobile applications and enterprise systems to improve productivity, reduce operational costs and enhance customer engagement.",
    problems: [
      "Employees spend significant time performing repetitive manual tasks that could be automated.",
      "Large volumes of business data are difficult to analyse and utilise effectively.",
      "Customer support teams struggle to respond quickly to increasing enquiries.",
      "Existing business systems lack intelligent capabilities that improve productivity and decision-making.",
    ],
    idealCustomers: [
      "Businesses seeking to automate repetitive business processes.",
      "Organisations processing large volumes of documents or business data.",
      "Companies enhancing customer service with AI-powered assistance.",
      "Enterprises integrating AI capabilities into existing software platforms.",
      "Startups building AI-powered digital products and services.",
    ],
    benefits: [
      {
          title: "Increase Productivity",
          description: "Automate repetitive activities, allowing employees to focus on higher-value work and strategic decision-making.",
          icon: Zap,
      },
      {
          title: "Improve Decision-Making",
          description: "Transform business data into meaningful insights that support faster and more informed decisions.",
          icon: LineChart,
      },
      {
          title: "Enhance Customer Experience",
          description: "Provide intelligent, personalised and responsive interactions through AI-powered features.",
          icon: Users,
      },
      {
          title: "Scale Business Operations",
          description: "Handle growing workloads efficiently without proportionally increasing operational resources.",
          icon: Rocket,
      },
    ],
    features: [
      {
          title: "AI Assistants & Chatbots",
          description: "Develop intelligent assistants that answer questions, guide users and automate routine interactions across websites, mobile apps and internal systems.",
      },
      {
          title: "Document Intelligence",
          description: "Extract, classify and process information from invoices, forms, contracts and other business documents using AI.",
      },
      {
          title: "Knowledge Search",
          description: "Enable employees and customers to find relevant information quickly through AI-powered semantic search across your business data.",
      },
      {
          title: "AI Workflow Integration",
          description: "Embed AI capabilities into existing business processes to automate approvals, recommendations and repetitive tasks.",
      },
      {
          title: "Natural Language Processing",
          description: "Understand, analyse and generate human language to support customer service, document processing and intelligent applications.",
      },
      {
          title: "Custom AI Solutions",
          description: "Design and implement AI capabilities tailored to your organisation's unique operational requirements and business goals.",
      },
    ],
    techStack: ["OpenAI", "n8n", "LangChain", "Vector Databases", "Python", "FastAPI"],
    workflow: DEV_WORKFLOW,
    faqs: [
      {
          question: "Can AI be integrated with our existing software?",
          answer: "Yes. We can integrate AI capabilities into your existing websites, mobile applications, ERP, CRM and other business systems without requiring a complete redevelopment. We assess your current architecture and recommend the most suitable integration approach for your business.",
      },
      {
          question: "Will AI replace our employees?",
          answer: "Our approach is to help AI assist your employees rather than replace them. We use AI to automate repetitive tasks, accelerate information processing and support decision-making, allowing your teams to focus on work that requires human expertise, creativity and judgement.",
      },
      {
          question: "Is our business data secure when using AI?",
          answer: "Yes. Protecting your business data is a priority. We implement secure authentication, encrypted communication, role-based access controls and carefully selected AI models and providers based on your security, privacy and compliance requirements.",
      },
      {
          question: "Can AI process our business documents?",
          answer: "Yes. AI can extract, classify, summarise and validate information from documents such as invoices, contracts, reports, forms and technical documents, significantly reducing manual processing effort.",
      },
      {
          question: "How do you determine whether AI is suitable for our business?",
          answer: "We begin by understanding your business processes, operational challenges and objectives. We then identify practical opportunities where AI can deliver measurable improvements in productivity, customer experience or operational efficiency, ensuring that every implementation provides genuine business value.",
      },
      {
          question: "Do you provide ongoing AI support and optimisation?",
          answer: "Yes. We continuously monitor AI performance, refine prompts and workflows, optimise integrations, implement model updates where appropriate and enhance AI capabilities as your business requirements evolve.",
      },
    ],
    relatedServices: ["web-applications", "workflow-automation", "api-development"],
    relatedIndustries: ["manufacturing", "healthcare", "fintech", "education"],
    caseConcept: {
      title: "AI-Powered Business Automation Platform",
      category: "Example Project",
      summary:
          "A growing organisation wanted to reduce manual processing of customer enquiries, business documents and internal workflows. We integrated AI capabilities into its existing business systems, enabling intelligent document processing, AI-assisted customer support, semantic knowledge search and workflow automation. The solution significantly improved productivity, reduced response times and allowed employees to focus on higher-value activities.",
      metrics: [
          {
              label: "Manual Processing",
              value: "-65%",
          },
          {
              label: "Response Time",
              value: "-80%",
          },
          {
              label: "Document Processing",
              value: "10,000+/Month",
          },
      ],
      tech: [
          "OpenAI",
          "LangChain",
          "n8n",
      ],
      accent: "#7C3AED",
    },
    image: "/images/services/ai-solutions.webp",
  },
  {
    slug: "workflow-automation",
    title: "Business Process Automation",
    category: "Operations",
    tagline: "Automate repetitive business processes, connect disconnected systems and improve operational efficiency through intelligent workflow automation.",
    icon: Workflow,
    accent: "#0F766E",
    overview:
      "Many business processes still rely on manual work, disconnected software and repetitive data entry, resulting in unnecessary delays, errors and operational costs. We design and implement workflow automation solutions that connect your business applications, automate routine processes and enable information to move seamlessly across systems. Whether using APIs, AI capabilities or modern automation platforms, we help organisations streamline operations, improve productivity and allow employees to focus on work that creates greater business value.",
    problems: [
      "Employees spend valuable time performing repetitive manual tasks.",
      "Business processes rely on disconnected systems and duplicate data entry.",
      "Approvals, notifications and reporting are delayed due to manual workflows.",
      "Operational errors increase because of inconsistent or repetitive human activities.",
    ],
    idealCustomers: [
      "Businesses seeking to eliminate repetitive manual processes.",
      "Organisations integrating multiple software systems.",
      "Companies improving operational efficiency across departments.",
      "Enterprises automating approval, reporting and notification workflows.",
      "Growing organisations scaling operations without proportionally increasing administrative effort.",
    ],
    benefits: [
      {
          title: "Increase Operational Efficiency",
          description: "Automate repetitive business activities so employees can focus on strategic and customer-focused work.",
          icon: Zap,
      },
      {
          title: "Reduce Manual Errors",
          description: "Standardise business processes to minimise data entry mistakes and improve operational consistency.",
          icon: ShieldCheck,
      },
      {
          title: "Accelerate Business Processes",
          description: "Automatically trigger approvals, notifications and data synchronisation to reduce processing time.",
          icon: Rocket,
      },
      {
          title: "Connect Business Applications",
          description: "Enable seamless communication between ERP, CRM, websites, mobile apps and third-party services.",
          icon: Layers,
      },
    ],
    features: [
      {
          title: "Business Process Automation",
          description: "Automate repetitive operational workflows including approvals, notifications, document routing and task assignments.",
      },
      {
          title: "Application Integration",
          description: "Connect ERP, CRM, accounting software, cloud services and other business systems using APIs and automation platforms.",
      },
      {
          title: "AI-Enhanced Workflows",
          description: "Integrate AI capabilities into automated workflows for document processing, intelligent routing and decision support.",
      },
      {
          title: "Scheduled & Event-Driven Automation",
          description: "Execute workflows automatically based on predefined schedules or real-time business events.",
      },
      {
          title: "Workflow Monitoring & Audit Trails",
          description: "Track workflow execution, identify bottlenecks and maintain complete audit logs for operational transparency.",
      },
      {
          title: "Custom Automation Solutions",
          description: "Design automation workflows tailored to your organisation's unique business processes and operational requirements.",
      },
    ],
    techStack: ["n8n", "FastAPI", "REST APIs", "OpenAI", "Docker", "PostgreSQL"],
    workflow: DEV_WORKFLOW,
    faqs: [
      {
          question: "What types of business processes can be automated?",
          answer: "Almost any repetitive, rule-based process can be automated. Common examples include approval workflows, employee onboarding, document processing, invoice handling, notifications, report generation, CRM updates, ERP integrations, customer enquiries and data synchronisation between business systems.",
      },
      {
          question: "Can workflow automation integrate with our existing software?",
          answer: "Yes. We can integrate workflow automation with your existing ERP, CRM, HRMS, accounting software, websites, mobile applications, cloud services and third-party platforms using APIs, webhooks and other integration technologies.",
      },
      {
          question: "Does workflow automation always require AI?",
          answer: "No. Many automation solutions rely on predefined business rules, APIs and system integrations without using AI. We introduce AI only where it provides clear business value, such as document processing, intelligent routing, content generation or natural language understanding.",
      },
      {
          question: "How do you identify processes suitable for automation?",
          answer: "We begin by analysing your existing business processes to identify repetitive tasks, manual approvals, duplicate data entry and integration gaps. We then recommend automation opportunities that deliver measurable improvements in productivity, accuracy and operational efficiency.",
      },
      {
          question: "Can automated workflows be modified as our business grows?",
          answer: "Yes. Our workflow solutions are designed to be flexible and scalable, allowing new approval steps, integrations, business rules and AI capabilities to be added as your operational requirements evolve.",
      },
      {
          question: "Do you provide ongoing support for automated workflows?",
          answer: "Yes. We provide monitoring, maintenance, optimisation and enhancements to ensure your automation workflows remain reliable, secure and aligned with changing business requirements.",
      },
    ],
    relatedServices: ["erp", "ai-integration", "api-development"],
    relatedIndustries: ["logistics", "fintech", "healthcare", "manufacturing", "education"],
    caseConcept: {
      title: "Business Process Automation Platform",
      category: "Example Project",
      summary:
          "A multi-department organisation relied on manual approvals, spreadsheet-based tracking and disconnected systems for procurement, employee onboarding and customer notifications. We implemented an automated workflow platform that integrated existing business applications, automated approvals, synchronised data between systems and delivered real-time notifications. The solution reduced administrative effort, improved process visibility and significantly accelerated day-to-day operations.",
      metrics: [
          {
              label: "Manual Tasks Eliminated",
              value: "75%",
          },
          {
              label: "Approval Time",
              value: "-60%",
          },
          {
              label: "Integrated Systems",
              value: "12",
          },
      ],
      tech: [
          "n8n",
          "FastAPI",
          "OpenAI",
      ],
      accent: "#0F766E",
    },
    image: "/images/services/business-process-automation.webp",
  },
  // {
  //   slug: "cloud-deployment",
  //   title: "Cloud Deployment",
  //   category: "Infrastructure",
  //   tagline: "Resilient, cost-efficient infrastructure with confident releases.",
  //   icon: Cloud,
  //   accent: "#0891B2",
  //   overview:
  //     "We set up cloud infrastructure and delivery pipelines that make releases boring in the best way — automated, safe and easy to roll back — while keeping costs in check.",
  //   problems: [
  //     "Risky, manual deployments that cause downtime.",
  //     "Surprise cloud bills with no clear cause.",
  //     "Scaling issues under real-world traffic.",
  //     "No clear recovery plan when something breaks.",
  //   ],
  //   idealCustomers: [
  //     "Teams shipping manually and fearing releases.",
  //     "Products facing growing or spiky traffic.",
  //     "Businesses wanting reliability without a big ops team.",
  //   ],
  //   benefits: [
  //     { title: "Confident releases", description: "Automated, repeatable deploys with easy rollback.", icon: ShieldCheck },
  //     { title: "Elastic scale", description: "Handle traffic spikes without falling over.", icon: Rocket },
  //     { title: "Lower cloud cost", description: "Right-sized infrastructure and cost visibility.", icon: Gauge },
  //     { title: "Less downtime", description: "Monitoring and recovery keep services available.", icon: Clock },
  //   ],
  //   features: [
  //     { title: "CI/CD pipelines", description: "Every change tested and shipped automatically." },
  //     { title: "Infrastructure as code", description: "Reproducible environments, no manual setup." },
  //     { title: "Auto-scaling", description: "Capacity that follows real demand." },
  //     { title: "Monitoring & alerts", description: "Know about issues before your users do." },
  //     { title: "Backups & recovery", description: "A tested plan for the day something goes wrong." },
  //   ],
  //   techStack: ["AWS", "GCP", "Docker", "Kubernetes", "Terraform", "GitHub Actions"],
  //   workflow: DEV_WORKFLOW,
  //   faqs: [
  //     { question: "Which cloud do you work with?", answer: "Primarily AWS and GCP, plus edge platforms. We choose based on your needs and existing stack." },
  //     { question: "Can you reduce our cloud costs?", answer: "Often, yes — right-sizing and cost visibility usually uncover meaningful savings." },
  //     { question: "Will deployments cause downtime?", answer: "We use strategies like blue-green and rolling releases to avoid downtime." },
  //     { question: "Do you set up monitoring?", answer: "Yes, with alerts so issues are caught early." },
  //     { question: "Can you migrate our existing infrastructure?", answer: "We handle migration to the cloud or between providers with minimal disruption." },
  //     { question: "Do we keep control of our infrastructure?", answer: "Yes — it's your accounts and code, documented for your team." },
  //   ],
  //   relatedServices: ["web-applications", "api-development", "maintenance-support"],
  //   relatedIndustries: ["saas", "fintech", "ecommerce"],
  //   caseConcept: {
  //     title: "Meridian — platform infrastructure",
  //     category: "Cloud Deployment",
  //     summary:
  //       "A concept infrastructure setup with CI/CD and auto-scaling that removed release downtime and cut monthly cloud spend.",
  //     metrics: [
  //       { label: "Deploy risk", value: "Low" },
  //       { label: "Cloud cost", value: "-28%" },
  //       { label: "Uptime", value: "99.95%" },
  //     ],
  //     tech: ["AWS", "Terraform", "Kubernetes"],
  //     accent: "#0891B2",
  //   },
  //   image: "/images/services/website-development.webp",
  // },
  {
    slug: "support",
    title: "Maintenance & Support",
    category: "Partnership",
    tagline: "Keep your business applications secure, reliable and performing at their best with proactive maintenance, continuous monitoring and expert technical support.",
    icon: LifeBuoy,
    accent: "#0F766E",
    overview:
      "Software requires continuous care to remain secure, reliable and aligned with changing business requirements. We provide proactive maintenance and support services that keep your websites, mobile applications, APIs and business systems operating efficiently. From security updates and performance optimisation to issue resolution and feature enhancements, we help organisations minimise downtime, reduce operational risks and maximise the long-term value of their software investments.",
    problems: [
      "Business applications become outdated due to missing security updates and regular maintenance.",
      "Unexpected system failures disrupt business operations and reduce productivity.",
      "Performance gradually declines as applications grow and user demand increases.",
      "Internal teams lack the time or expertise to maintain complex software systems.",
    ],
    idealCustomers: [
      "Businesses operating mission-critical software applications.",
      "Organisations requiring long-term technical support for websites, mobile apps or enterprise systems.",
      "Companies without an in-house software maintenance team.",
      "Growing businesses continuously improving their digital platforms.",
      "Organisations seeking proactive monitoring and preventative maintenance.",
    ],
    benefits: [
      {
          title: "Maximise System Reliability",
          description: "Keep your applications operating consistently through proactive monitoring, maintenance and timely issue resolution.",
          icon: ShieldCheck,
      },
      {
          title: "Reduce Operational Risk",
          description: "Regular updates, security improvements and preventative maintenance minimise unexpected system failures.",
          icon: Gauge,
      },
      {
          title: "Improve Performance",
          description: "Continuous optimisation ensures your applications remain responsive as users, data volumes and business requirements grow.",
          icon: Rocket,
      },
      {
          title: "Protect Your Software Investment",
          description: "Extend the useful life of your applications through ongoing improvements, enhancements and technical support.",
          icon: Layers,
      },
    ],
    features: [
      {
          title: "Application Monitoring",
          description: "Continuously monitor application health, availability and performance to identify and resolve issues before they impact your business.",
      },
      {
          title: "Security Updates",
          description: "Apply security patches, dependency updates and vulnerability fixes to keep your applications protected against emerging threats.",
      },
      {
          title: "Performance Optimisation",
          description: "Analyse and improve application performance, database efficiency and resource utilisation to maintain a fast and responsive user experience.",
      },
      {
          title: "Bug Fixes & Issue Resolution",
          description: "Investigate, prioritise and resolve software defects quickly to minimise disruption to business operations.",
      },
      {
          title: "Feature Enhancements",
          description: "Implement new functionality and improvements that support changing business requirements and user expectations.",
      },
      {
          title: "Technical Support",
          description: "Provide expert assistance for troubleshooting, incident management, system configuration and operational guidance.",
      },
    ],
    techStack: ["Docker", "Nginx", "GitHub Actions", "AWS", "PostgreSQL", "Redis"],
    workflow: DEV_WORKFLOW,
    faqs: [
      {
          question: "Do you provide maintenance for software developed by other companies?",
          answer: "Yes. We can maintain and enhance applications developed by other vendors after first reviewing the codebase, infrastructure, documentation and overall system architecture. Following the assessment, we recommend an appropriate support and maintenance plan.",
      },
      {
          question: "What is included in your maintenance service?",
          answer: "Our maintenance services can include application monitoring, security updates, bug fixes, performance optimisation, database maintenance, dependency updates, infrastructure support, feature enhancements and technical assistance. The exact scope depends on the agreed support plan and your business requirements.",
      },
      {
          question: "How quickly do you respond to reported issues?",
          answer: "Response times depend on the agreed Service Level Agreement (SLA). Critical issues affecting business operations receive the highest priority, while less urgent requests are addressed according to their severity and business impact.",
      },
      {
          question: "Can you add new features after the application is deployed?",
          answer: "Yes. Software should evolve alongside your business. We provide ongoing enhancement services to implement new features, integrate additional systems and improve existing functionality as your operational requirements change.",
      },
      {
          question: "Do you monitor applications proactively?",
          answer: "Yes. Where included in the support agreement, we implement monitoring to track application availability, performance and operational health, allowing potential issues to be identified and resolved before they significantly impact users.",
      },
      {
          question: "Can we choose different levels of support?",
          answer: "Yes. We can provide flexible maintenance arrangements based on your operational needs, ranging from basic corrective maintenance to comprehensive managed support that includes proactive monitoring, regular updates, performance optimisation and ongoing technical consultancy.",
      },
    ],
    relatedServices: ["cloud-deployment", "web-applications", "mobile-app-development"],
    relatedIndustries: ["manufacturing", "healthcare", "education", "government"],
    caseConcept: {
      title: "Enterprise Application Support Programme",
      category: "Example Project",
      summary:
          "A multi-location organisation depended on several business-critical applications developed over many years. We established a proactive maintenance and support programme covering application monitoring, security updates, performance optimisation, issue resolution and ongoing feature enhancements. This ensured reliable day-to-day operations, improved system performance and enabled the organisation to introduce new capabilities without disrupting existing business processes.",
      metrics: [
          {
              label: "Application Availability",
              value: "99.9%",
          },
          {
              label: "Critical Issues Resolved",
              value: "<4 Hours",
          },
          {
              label: "Supported Applications",
              value: "15",
          },
      ],
      tech: [
          "Docker",
          "AWS",
          "PostgreSQL",
      ],
      accent: "#2563EB",
    },
    image: "/images/services/support-maintenance.webp",
  },
];
